// Morning Briefing — UI state. Session-only except the two localStorage keys below.
import { create } from 'zustand';

const SEEN_KEY = 'clb.briefing.seen.v1';       // + `.${userId}` → 'YYYY-MM-DD': shown once per local day, per person
const SNOOZE_KEY = 'clb.briefing.snooze.v1';   // { [recipientId]: 'YYYY-MM-DD' } hidden until that day
const REMIND_KEY = 'clb.briefing.remind.v1';   // + `.${userId}` → epoch ms of a "Remind me at 14:00"

const read = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, v); } catch (e) { /* private window: the briefing still works, it may show again */ } };

const memSeen = {};                              // per page load: a failed write can never make it open twice
export const seenToday = (userId, day) => memSeen[userId] === day || read(`${SEEN_KEY}.${userId}`) === day;
export const markSeen = (userId, day) => { memSeen[userId] = day; write(`${SEEN_KEY}.${userId}`, day); };
export const getSnoozed = () => { try { return JSON.parse(read(SNOOZE_KEY) || '{}') || {}; } catch (e) { return {}; } };
export const setSnoozed = (map) => write(SNOOZE_KEY, JSON.stringify(map));
export const getRemind = (userId) => Number(read(`${REMIND_KEY}.${userId}`)) || 0;
export const setRemind = (userId, ts) => write(`${REMIND_KEY}.${userId}`, String(ts || 0));
/** Ids snoozed past `day` (YYYY-MM-DD); expired entries are dropped. */
export const activeSnoozed = (day) => { const m = getSnoozed(); const keep = {}; Object.keys(m).forEach((k) => { if (m[k] > day) keep[k] = m[k]; }); if (Object.keys(keep).length !== Object.keys(m).length) setSnoozed(keep); return keep; };

export const useBriefingStore = create((set) => ({
  open: false,
  snapshot: [],            // the rows the briefing shows (kept while open, so handled items stay listed)
  handled: [],             // recipient ids dealt with this session (done or snoozed)
  show: (rows) => set((s) => {
    const seen = new Set(rows.map((r) => r.id));
    return { open: true, snapshot: rows.concat(s.snapshot.filter((r) => !seen.has(r.id))) };
  }),
  hide: () => set({ open: false, snapshot: [], handled: [] }),   // closed = finished: nothing carries over to a reopen
  handle: (id) => set((s) => (s.handled.includes(id) ? s : { handled: s.handled.concat(id) })),
  unhandle: (id) => set((s) => ({ handled: s.handled.filter((x) => x !== id) })),
  reset: () => set({ open: false, snapshot: [], handled: [] }),
  // The gate publishes who may see the briefing and how many items wait, so the bell entry needs no query client.
  allowed: false, count: 0, requested: false,
  publish: (allowed, count) => set((s) => (s.allowed === allowed && s.count === count ? s : { allowed, count })),
  request: () => set({ requested: true }),
  clearRequest: () => set({ requested: false }),
}));
